import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { EventSettings as EventSettingsType } from '../types';

const EventSettings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [botEnabled, setBotEnabled] = useState(false);

  // Event announcement toggles
  const [announceFollowers, setAnnounceFollowers] = useState(false);
  const [announceSubscribers, setAnnounceSubscribers] = useState(false);
  const [announceGifts, setAnnounceGifts] = useState(false);
  const [announceMilestones, setAnnounceMilestones] = useState(false);
  const [useAiResponses, setUseAiResponses] = useState(false);

  // Milestone thresholds
  const [viewerMilestones, setViewerMilestones] = useState<number[]>([10, 25, 50, 100, 250, 500, 1000, 2500, 5000]);
  const [followerMilestones, setFollowerMilestones] = useState<number[]>([10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000]);
  const [subscriberMilestones, setSubscriberMilestones] = useState<number[]>([5, 10, 25, 50, 100, 250, 500, 1000]);

  // Input states for adding new milestones
  const [newViewerMilestone, setNewViewerMilestone] = useState('');
  const [newFollowerMilestone, setNewFollowerMilestone] = useState('');
  const [newSubscriberMilestone, setNewSubscriberMilestone] = useState('');

  // Load event settings on mount
  useEffect(() => {
    const loadEventSettings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_rumble_settings')
          .select('event_settings, bot_enabled')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error loading event settings:', error);
          return;
        }

        // Check if chat bot is enabled
        setBotEnabled(data?.bot_enabled ?? false);

        if (data?.event_settings) {
          const settings = data.event_settings as EventSettingsType;
          setAnnounceFollowers(settings.announce_followers ?? false);
          setAnnounceSubscribers(settings.announce_subscribers ?? false);
          setAnnounceGifts(settings.announce_gifts ?? false);
          setAnnounceMilestones(settings.announce_milestones ?? false);
          // Only set useAiResponses if bot is enabled
          setUseAiResponses(data?.bot_enabled ? (settings.use_ai_responses ?? false) : false);

          if (settings.milestone_thresholds) {
            setViewerMilestones(settings.milestone_thresholds.viewers || [10, 25, 50, 100, 250, 500, 1000, 2500, 5000]);
            setFollowerMilestones(settings.milestone_thresholds.followers || [10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000]);
            setSubscriberMilestones(settings.milestone_thresholds.subscribers || [5, 10, 25, 50, 100, 250, 500, 1000]);
          }
        }
      } catch (err) {
        console.error('Failed to load event settings:', err);
      } finally {
        setLoading(false);
      }
    };

    loadEventSettings();
  }, [user]);

  // Auto-save event settings
  const autoSave = async (settings: Partial<EventSettingsType>) => {
    if (!user) return;

    const eventSettings: EventSettingsType = {
      announce_followers: settings.announce_followers ?? announceFollowers,
      announce_subscribers: settings.announce_subscribers ?? announceSubscribers,
      announce_gifts: settings.announce_gifts ?? announceGifts,
      announce_milestones: settings.announce_milestones ?? announceMilestones,
      // Only allow use_ai_responses if bot is enabled
      use_ai_responses: botEnabled ? (settings.use_ai_responses ?? useAiResponses) : false,
      milestone_thresholds: settings.milestone_thresholds ?? {
        viewers: viewerMilestones,
        followers: followerMilestones,
        subscribers: subscriberMilestones
      }
    };

    try {
      const { error } = await supabase
        .from('user_rumble_settings')
        .update({ event_settings: eventSettings })
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (err) {
      console.error('Failed to auto-save event settings:', err);
    }
  };

  // Add milestone helper with auto-save
  const addMilestone = async (value: string, currentMilestones: number[], setMilestones: (m: number[]) => void, setClearInput: (v: string) => void, type: 'viewers' | 'followers' | 'subscribers') => {
    const num = parseInt(value);
    if (!isNaN(num) && num > 0 && !currentMilestones.includes(num)) {
      const updated = [...currentMilestones, num].sort((a, b) => a - b);
      setMilestones(updated);
      setClearInput('');

      // Auto-save with updated thresholds
      await autoSave({
        milestone_thresholds: {
          viewers: type === 'viewers' ? updated : viewerMilestones,
          followers: type === 'followers' ? updated : followerMilestones,
          subscribers: type === 'subscribers' ? updated : subscriberMilestones
        }
      });
    }
  };

  // Remove milestone helper with auto-save
  const removeMilestone = async (value: number, currentMilestones: number[], setMilestones: (m: number[]) => void, type: 'viewers' | 'followers' | 'subscribers') => {
    const updated = currentMilestones.filter(m => m !== value);
    setMilestones(updated);

    // Auto-save with updated thresholds
    await autoSave({
      milestone_thresholds: {
        viewers: type === 'viewers' ? updated : viewerMilestones,
        followers: type === 'followers' ? updated : followerMilestones,
        subscribers: type === 'subscribers' ? updated : subscriberMilestones
      }
    });
  };

  if (loading) {
    return (
      <div className="paint-card p-4">
        <h3 className="text-lg font-medium mb-4 text-white border-b-2 border-white pb-2">
          event settings
        </h3>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-700 rounded"></div>
          <div className="h-8 bg-gray-700 rounded"></div>
          <div className="h-8 bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="paint-card p-4">
      <h3 className="text-lg font-medium mb-4 text-white border-b-2 border-white pb-2">
        event settings
      </h3>

      <div className="space-y-6">
        {/* Event Announcements Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-3">announcements</h4>
          <div className="space-y-3">
            <label className="paint-label inline-flex items-center justify-between">
              <span>announce new followers</span>
              <label className="toggle-switch ml-4">
                <input
                  type="checkbox"
                  checked={announceFollowers}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setAnnounceFollowers(value);
                    await autoSave({ announce_followers: value });
                  }}
                  disabled={!user}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
            <p className="text-xs text-gray-500 -mt-2">announce when someone follows your channel</p>

            <label className="paint-label inline-flex items-center justify-between">
              <span>announce new subscribers</span>
              <label className="toggle-switch ml-4">
                <input
                  type="checkbox"
                  checked={announceSubscribers}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setAnnounceSubscribers(value);
                    await autoSave({ announce_subscribers: value });
                  }}
                  disabled={!user}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
            <p className="text-xs text-gray-500 -mt-2">announce when someone subscribes to your channel</p>

            <label className="paint-label inline-flex items-center justify-between">
              <span>announce gifted subs</span>
              <label className="toggle-switch ml-4">
                <input
                  type="checkbox"
                  checked={announceGifts}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setAnnounceGifts(value);
                    await autoSave({ announce_gifts: value });
                  }}
                  disabled={!user}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
            <p className="text-xs text-gray-500 -mt-2">announce when someone gifts subscriptions to your channel</p>

            <label className="paint-label inline-flex items-center justify-between">
              <span>announce milestones</span>
              <label className="toggle-switch ml-4">
                <input
                  type="checkbox"
                  checked={announceMilestones}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setAnnounceMilestones(value);
                    await autoSave({ announce_milestones: value });
                  }}
                  disabled={!user}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
            <p className="text-xs text-gray-500 -mt-2">announce when you hit viewer/follower/subscriber milestones</p>

            <label className={`paint-label inline-flex items-center justify-between ${!botEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <span>use ai responses</span>
              <label className="toggle-switch ml-4">
                <input
                  type="checkbox"
                  checked={useAiResponses}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setUseAiResponses(value);
                    await autoSave({ use_ai_responses: value });
                  }}
                  disabled={!user || !botEnabled}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
            {!botEnabled ? (
              <p className="text-xs text-amber-500 -mt-2">
                <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                chat bot extension must be enabled to use ai responses. go to extensions and enable the chat bot.
              </p>
            ) : (
              <p className="text-xs text-gray-500 -mt-2">use ai-generated personalized responses for announcements</p>
            )}
          </div>
        </div>

        {/* Milestone Thresholds Section */}
        {announceMilestones && (
          <div className="border-t border-gray-700 pt-6">
            <h4 className="text-sm font-medium text-gray-300 mb-3">milestone thresholds</h4>

            {/* Viewer Milestones */}
            <div className="mb-4">
              <label className="paint-label text-sm">viewer milestones:</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="number"
                  value={newViewerMilestone}
                  onChange={(e) => setNewViewerMilestone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addMilestone(newViewerMilestone, viewerMilestones, setViewerMilestones, setNewViewerMilestone, 'viewers');
                    }
                  }}
                  className="paint-input flex-grow"
                  placeholder="Enter milestone number"
                  min="1"
                  disabled={!user}
                />
                <button
                  type="button"
                  onClick={() => addMilestone(newViewerMilestone, viewerMilestones, setViewerMilestones, setNewViewerMilestone, 'viewers')}
                  className="paint-button paint-button-sm"
                  disabled={!newViewerMilestone || !user}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {viewerMilestones.map((milestone) => (
                  <div
                    key={milestone}
                    className="inline-flex items-center gap-2 bg-gray-900 px-3 py-1 rounded border border-gray-700"
                  >
                    <span className="text-white text-sm font-mono">{milestone.toLocaleString()}</span>
                    <button
                      type="button"
                      onClick={() => removeMilestone(milestone, viewerMilestones, setViewerMilestones, 'viewers')}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="remove threshold"
                      disabled={!user}
                    >
                      <i className="fa-solid fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">announce when you reach these viewer counts</p>
            </div>

            {/* Follower Milestones */}
            <div className="mb-4">
              <label className="paint-label text-sm">follower milestones:</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="number"
                  value={newFollowerMilestone}
                  onChange={(e) => setNewFollowerMilestone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addMilestone(newFollowerMilestone, followerMilestones, setFollowerMilestones, setNewFollowerMilestone, 'followers');
                    }
                  }}
                  className="paint-input flex-grow"
                  placeholder="Enter milestone number"
                  min="1"
                  disabled={!user}
                />
                <button
                  type="button"
                  onClick={() => addMilestone(newFollowerMilestone, followerMilestones, setFollowerMilestones, setNewFollowerMilestone, 'followers')}
                  className="paint-button paint-button-sm"
                  disabled={!newFollowerMilestone || !user}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {followerMilestones.map((milestone) => (
                  <div
                    key={milestone}
                    className="inline-flex items-center gap-2 bg-gray-900 px-3 py-1 rounded border border-gray-700"
                  >
                    <span className="text-white text-sm font-mono">{milestone.toLocaleString()}</span>
                    <button
                      type="button"
                      onClick={() => removeMilestone(milestone, followerMilestones, setFollowerMilestones, 'followers')}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="remove threshold"
                      disabled={!user}
                    >
                      <i className="fa-solid fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">announce when you reach these follower counts</p>
            </div>

            {/* Subscriber Milestones */}
            <div>
              <label className="paint-label text-sm">subscriber milestones:</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="number"
                  value={newSubscriberMilestone}
                  onChange={(e) => setNewSubscriberMilestone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addMilestone(newSubscriberMilestone, subscriberMilestones, setSubscriberMilestones, setNewSubscriberMilestone, 'subscribers');
                    }
                  }}
                  className="paint-input flex-grow"
                  placeholder="Enter milestone number"
                  min="1"
                  disabled={!user}
                />
                <button
                  type="button"
                  onClick={() => addMilestone(newSubscriberMilestone, subscriberMilestones, setSubscriberMilestones, setNewSubscriberMilestone, 'subscribers')}
                  className="paint-button paint-button-sm"
                  disabled={!newSubscriberMilestone || !user}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {subscriberMilestones.map((milestone) => (
                  <div
                    key={milestone}
                    className="inline-flex items-center gap-2 bg-gray-900 px-3 py-1 rounded border border-gray-700"
                  >
                    <span className="text-white text-sm font-mono">{milestone.toLocaleString()}</span>
                    <button
                      type="button"
                      onClick={() => removeMilestone(milestone, subscriberMilestones, setSubscriberMilestones, 'subscribers')}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="remove threshold"
                      disabled={!user}
                    >
                      <i className="fa-solid fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">announce when you reach these subscriber counts</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventSettings;
